import { useQuery } from "@tanstack/react-query";
import { FlaskConical } from "lucide-react";
import { useApi } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { cn } from "@ncp/ui";

/** One control the test-environment decision controls relax, in both languages. */
export interface DecisionRelaxation { code: string; en: string; ar: string }

/** The decision controls in force (setting procedure.decisionControls, Platform Console › Settings › Procedural fairness). */
export interface DecisionControlsView {
  key: string;
  value: "Strict" | "TestEnvironment";
  relaxed: boolean;
  available: boolean;
  isDefault: boolean;
  default: string;
  /** The signed-in officer is the Director or above and stands in for every role under the test-environment controls. */
  headStandsIn: boolean;
  relaxations: DecisionRelaxation[];
}

export function useDecisionControls() {
  const api = useApi();
  return useQuery({ queryKey: ["decision-controls"], queryFn: () => api.get<DecisionControlsView>("/api/platform/decision-controls"), staleTime: 60_000 });
}

/**
 * Under the test-environment decision controls the Director, the Assistant Under-Secretary, the Under-Secretary and the Minister take every act,
 * step and approval. The server decides; this only opens the screens' controls to them.
 */
export function useHeadStandsIn(): boolean {
  return useDecisionControls().data?.headStandsIn ?? false;
}

/**
 * The quiet line the Decision tab and "Before signature" show while the test-environment decision controls are in force, naming what they relax, so
 * nobody mistakes it for production behaviour. Nothing under Strict.
 */
export function TestEnvironmentLine({ relaxations, className }: { relaxations?: DecisionRelaxation[] | null; className?: string }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const ar = locale === "ar";
  const controls = useDecisionControls();
  const list = relaxations ?? (controls.data?.relaxed ? controls.data.relaxations : []);
  if (list.length === 0) return null;
  return (
    <p role="note" className={cn("flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground", className)}>
      <FlaskConical className="mt-0.5 size-3 shrink-0" aria-hidden />
      <span>
        <span className="font-medium">{t("backoffice:decisionControls.testEnvironment.line")}</span>{" "}
        {list.map((r, i) => (
          <span key={r.code} title={ar ? r.ar : r.en}>{i > 0 ? (ar ? "؛ " : "; ") : ""}{t(`backoffice:decisionControls.testEnvironment.codes.${r.code}`, { defaultValue: ar ? r.ar : r.en })}</span>
        ))}
      </span>
    </p>
  );
}
